import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { PageHeader } from "@/components/app/shell/page-header";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { ReportTable, type ReportColumn } from "@/components/app/documents/report-table";
import { StagePill } from "@/routes/_authenticated/_financials/expense-entries/-components/payment-stage";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { useFacility } from "@/lib/facility/facility";
import { orpc, orpcClient, type Output } from "@/lib/orpc";
import { optionalMonth, optionalOneOf, KEEP_PLACE } from "@/lib/search";
import { Age } from "@/routes/_authenticated/_financials/ap-report/-components/ap-report-age";

type PayableRow = Output<typeof orpcClient.financials.reports.accountsPayable>["items"][number];

type ApSearch = { period?: string; status?: Status };

export const Route = createFileRoute("/_authenticated/_financials/ap-report/")({
  validateSearch: (search: Record<string, unknown>): ApSearch => ({
    period: optionalMonth(search.period),
    status: optionalOneOf(
      STATUSES.map((entry) => entry.key),
      search.status,
    ),
  }),
  component: AccountsPayable,
});

const now = new Date();
/* A tile that filters is a button, so it shows keyboard focus like one; its
   card fills the row so a one-line hint does not leave a shorter tile. */
const tileButton =
  "flex flex-col rounded-xl text-left outline-hidden *:flex-1 focus-visible:ring-[3px] focus-visible:ring-ring/80";

const ageInDays = (date: string) =>
  Math.max(0, Math.round((Date.now() - new Date(`${date}T00:00:00Z`).getTime()) / 86_400_000));

/* The four states an expense passes through, in the order it passes through them. A bill nobody has
 * touched is somebody's job this afternoon; a payment already initiated is one nobody should start
 * again. Collapsing those two into "outstanding" is what made the old single tile useless. */
const STATUSES = [
  { key: "created", label: "Created / pending", hint: "Recorded, payment not started" },
  { key: "payment_initiated", label: "Payment initiated", hint: "Started, not yet confirmed sent" },
  { key: "payment_sent", label: "Payment sent", hint: "Gone; no longer outstanding" },
  { key: "voided", label: "Voided", hint: "Cancelled, and never owed" },
] as const;

type Status = (typeof STATUSES)[number]["key"];

/** Built outside the component: a table of columns is not itself a component. */
const COLUMNS: ReadonlyArray<ReportColumn<PayableRow>> = [
  { header: "Date", cell: (row) => row.transactionDate },
  { header: "Vendor", cell: (row) => row.vendor ?? "-" },
  { header: "Category", cell: (row) => row.category },
  {
    header: "Status",
    // The same pill the expense entries page draws for the same stage.
    cell: (row) => <StagePill stage={row.status} />,
    value: (row) => STATUSES.find((entry) => entry.key === row.status)?.label ?? row.status,
  },
  { header: "Ref #", cell: (row) => row.referenceNumber ?? "-" },
  { header: "Initiated", cell: (row) => row.paymentInitiatedDate ?? "-" },
  { header: "Sent", cell: (row) => row.paymentSentDate ?? "-" },
  { header: "Amount", cell: (row) => row.amount, align: "right" },
  {
    /* How long a bill has been sitting there is the reason to pick it
             up next, and it is the one thing the date column cannot say. */
    header: "Age",
    cell: (row) => <Age days={ageInDays(row.transactionDate)} paid={row.status !== "created"} />,
    value: (row) => ageInDays(row.transactionDate),
    align: "right",
  },
];

/** Where the month's expenses have got to. */
function AccountsPayable() {
  const { facilityId } = useFacility();
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<ApSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const thisMonth = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, "0")}`;
  const chosen = params.period ?? thisMonth;
  const year = Number(chosen.slice(0, 4));
  const month = Number(chosen.slice(5, 7));
  const status: Status | "all" = params.status ?? "all";
  const setStatus = (next: Status | "all") => patch({ status: next === "all" ? undefined : next });

  const report = useQuery(
    orpc.financials.reports.accountsPayable.queryOptions({ input: { facilityId, year, month } }),
  );
  const period = `${year}-${String(month).padStart(2, "0")}`;

  return (
    <>
      <PageHeader title="AP report" meta="Expenses recorded but not yet paid." />

      <div className="flex items-center gap-2">
        <Label className="text-muted-foreground" htmlFor="ap-period">
          Month
        </Label>
        <Input
          id="ap-period"
          type="month"
          value={period}
          onChange={(event) => {
            const [nextYear, nextMonth] = event.target.value.split("-");
            if (nextYear && nextMonth) {
              const next = `${nextYear}-${nextMonth}`;
              patch({ period: next === thisMonth ? undefined : next });
            }
          }}
          className="w-40 pointer-coarse:w-48"
        />
      </div>

      {/* The tile is the filter: a total nobody can open is a number somebody
          has to go and reconstruct by hand. */}
      {report.data ? (
        <StatTiles>
          <button
            type="button"
            aria-pressed={status === "all"}
            className={cn(tileButton, status === "all" && "ring-primary ring-2")}
            onClick={() => setStatus("all")}
          >
            <StatTile label="Outstanding" value={report.data.total} hint="Pending plus initiated" />
          </button>
          {STATUSES.map((entry) => (
            <button
              key={entry.key}
              type="button"
              aria-pressed={status === entry.key}
              className={cn(tileButton, status === entry.key && "ring-primary ring-2")}
              onClick={() => setStatus(status === entry.key ? "all" : entry.key)}
            >
              <StatTile
                label={entry.label}
                value={report.data.buckets[entry.key].total}
                hint={`${report.data.buckets[entry.key].count} ${
                  report.data.buckets[entry.key].count === 1 ? "entry" : "entries"
                }, ${entry.hint}`}
              />
            </button>
          ))}
        </StatTiles>
      ) : null}

      {/* A failed read is not an empty report. */}
      {report.isError ? (
        <ReadFailed
          message="Expenses could not be loaded."
          error={report.error}
          onRetry={() => void report.refetch()}
          retrying={report.isFetching}
        />
      ) : (
        <ReportTable
          rows={
            status === "all"
              ? report.data?.items
              : report.data?.items.filter((row) => row.status === status)
          }
          pending={report.isPending}
          filename={`ap-${period}.csv`}
          noun="bills"
          empty={
            status === "all"
              ? {
                  title: "No expenses this month",
                  description: "Bills recorded on Expense entries appear here until they are paid.",
                }
              : {
                  title: "Nothing in this status",
                  description: "Choose Outstanding above to see every bill for the month.",
                }
          }
          columns={COLUMNS}
        />
      )}
    </>
  );
}
